import React, {useEffect} from "react";
import {Alert, Button, Card, Col, Form, Row} from "react-bootstrap";
import ActsService from "../../services/acts.service";
import ReactQuill from "react-quill";
import 'react-quill/dist/quill.snow.css';
import {
    ACT_SIZES,
    ACT_SOUND_SYSTEM,
    GENRES, isLoggedIn,
    isLoggedInWithRole,
    isLoggedInWithRoleAndUserIs,
    toStrArray
} from "../../common/utils";
import {useForm} from "react-hook-form"
import AuthService from "../../services/auth.service";
import {API_EDITS_ALLOWED} from "../../services/axios.utils";
import {Link} from "react-router-dom";

const ActEdit = ({act}) => {
    const {
        register,
        handleSubmit,
        setValue,
        formState: {errors},
    } = useForm()

    let canEdit = isLoggedInWithRoleAndUserIs('performer', act.creator_id);
    let canEditNew = isLoggedInWithRole('performer');
    let allowEdit = canEdit || (canEditNew && (act.id === undefined));

    let user = AuthService.getCurrentUser();
    let loggedIn = isLoggedIn();
    let editsAllowed = API_EDITS_ALLOWED === "true" || (loggedIn && user.is_admin);

    const reactQuillRef = React.useRef();

    // limit to 1500 characters
    const checkCharacterCount = (event) => {
        // event.preventDefault();
        const unprivilegedEditor = reactQuillRef.current.unprivilegedEditor;
        if (unprivilegedEditor.getLength() > 1500 && event.key !== 'Backspace') {
            window.alert("You've reached the maximum character count of 1500.")
        }
    };

    const onEditorStateChange = (editorState) => {
        // disabling matchVisual did not work: https://stackoverflow.com/questions/43748108/how-to-keep-quill-from-inserting-blank-paragraphs-pbr-p-before-heading
        if (editorState === "<p><br></p>") {
            editorState = ""
        }
        setValue("actBio", editorState);
    };

    const handleCancel = (event) => {
        event.preventDefault();
        window.location.assign("/acts");
    }

    // cannot register actBio like the other fields because the input type is react-quill
    useEffect(() => {
        register("actBio", {required: true});
        setValue("actBio", act.bio);
    }, [register]);  // eslint-disable-line react-hooks/exhaustive-deps

    // todo: handle backend errors (non-200 or 204)
    // todo: validate address is unique (integrity error)
    const handleSubmitRHF = (data) => {
        console.log("=== data: " + JSON.stringify(data));

        let payload = {
            name: data.actName,
            bio: data.actBio,
            type: data.actType,
            rain_date_available: data.actRainDate,
            preferred_slots: toStrArray(data.actPrefSlots),
            size: data.actSize,
            sound_system: data.actSoundSystem,
            venue_pref: data.actVenuePref,
        }
        console.log("payload: " + JSON.stringify(payload))

        if (act.id === undefined) {
            ActsService.createAct(payload).then(
                response => {
                    console.log("act:" + JSON.stringify(response.data));
                    // todo: pass redirect url since could be coming from "/myacts"
                    window.location.assign("/acts");
                },
                error => {
                    console.log(error);
                });

        } else {
            ActsService.updateAct(act.id, payload).then(
                response => {
                    console.log("act:" + JSON.stringify(response.data));
                    // todo: pass redirect url since could be coming from "/myacts"
                    window.location.assign("/acts");
                },
                error => {
                    console.log(error);
                });
        }
    }

    return (
        <>
            {!allowEdit &&
                <div>
                    You are not allowed to perform this action.
                </div>
            }
            {allowEdit &&
                <div>
                    {!editsAllowed &&
                        <Alert variant="danger">
                            Registration is closed for this year's Porchfest. If you need to modify your act,
                            please <Link to="/contact" reloadDocument="true">contact us</Link>.
                        </Alert>
                    }

                    {/*"handleSubmit" will validate your inputs before invoking "onSubmit"*/}
                    <Form onSubmit={handleSubmit(handleSubmitRHF)}>

                        <Row className="mb-3">
                            <Col>
                                <div>Please fill out the form below:</div>
                                <div className="text-danger">
                                    <span className="fs-5 text">*</span> Indicates a required field
                                </div>
                            </Col>
                        </Row>

                        <Card>
                            <Card.Body>
                                <div className="mb-3">
                                    <strong>What is the name of your act?</strong>&nbsp;<span className="fs-5 text text-danger">*</span>
                                </div>
                                <div>
                                    {/* register your input into the hook by invoking the "register" function */}
                                    <Form.Control
                                        type="text"
                                        defaultValue={act.name}
                                        placeholder="Enter your act name"
                                        {...register("actName", {required: true})}
                                    />
                                    {errors.actName && <span className="pf-validation-error">This field is required</span>}
                                </div>
                            </Card.Body>
                        </Card>

                        <Card>
                            <Card.Body>
                                <div className="mb-3">
                                    <strong>How do you describe your act?</strong>&nbsp;<span className="fs-5 text text-danger">*</span>
                                </div>
                                <div>
                                    {/* see: https://github.com/zenoamaro/react-quill */}
                                    Use the area below to provide:
                                    <ul>
                                        <li>A brief description of your act</li>
                                        <li>The names of performers and/or instruments</li>
                                        <li>Information on any accommodations you will need for your performance</li>
                                        <li>Links to your website or social media</li>
                                    </ul>
                                    <ReactQuill theme="snow"
                                                defaultValue={act.bio}
                                                onChange={onEditorStateChange}
                                                modules={{
                                                    toolbar: [
                                                        // [{ 'header': [1, 2, false] }],
                                                        ['bold', 'italic', 'underline'],
                                                        [{'list': 'ordered'}, {'list': 'bullet'}],
                                                        ['link'],
                                                        ['clean']
                                                    ],
                                                    // this is needed to eliminate the <p><br></p> problem
                                                    clipboard: {
                                                        matchVisual: false
                                                    }
                                                }}
                                                // placeholder={""}
                                                onKeyDown={checkCharacterCount}
                                                ref={reactQuillRef}
                                    />
                                    {errors.actBio && <span className="pf-validation-error">This field is required</span>}
                                </div>
                            </Card.Body>
                        </Card>

                        <Card>
                            <Card.Body>
                                <div className="mb-3">
                                    <strong>What is your size of your act?</strong>&nbsp;<span className="fs-5 text text-danger">*</span>
                                </div>
                                <div>
                                    <Form.Select
                                        defaultValue={act.size}
                                        {...register("actSize", {required: true})}>
                                        {ACT_SIZES.map((s, index) => (
                                            <option key={index} value={s.value}>{s.name}</option>
                                        ))}
                                    </Form.Select>
                                    {errors.actSize && <span className="pf-validation-error">This field is required</span>}
                                </div>
                            </Card.Body>
                        </Card>

                        <Card>
                            <Card.Body>
                                <div className="mb-3">
                                    <strong>What is the genre of your act?</strong>&nbsp;<span className="fs-5 text text-danger">*</span>
                                </div>
                                <div>
                                    <Form.Select
                                        defaultValue={act.type}
                                        {...register("actType", {required: true})}>
                                        {GENRES.map((g, index) => (
                                            <option key={index} value={g.value}>{g.name}</option>
                                        ))}
                                    </Form.Select>
                                    {errors.actType && <span className="pf-validation-error">This field is required</span>}
                                </div>
                            </Card.Body>
                        </Card>

                        <Card>
                            <Card.Body>
                                <div className="mb-3">
                                    <strong>What time slots are you able to perform?</strong>&nbsp;<span className="fs-5 text text-danger">*</span>
                                </div>
                                <div>
                                    <div>
                                        <Form.Check
                                            label="Slot 1: 12:00pm - 1:00pm"
                                            defaultChecked={act.preferred_slots.includes("Slot 1")}
                                            {...register("actPrefSlots", {required: true})}
                                            value="Slot 1"
                                        />
                                    </div>
                                    <div>
                                        <Form.Check
                                            label="Slot 2: 1:00pm - 2:00pm"
                                            defaultChecked={act.preferred_slots.includes("Slot 2")}
                                            {...register("actPrefSlots", {required: true})}
                                            value="Slot 2"
                                        />
                                    </div>
                                    <div>
                                        <Form.Check
                                            label="Slot 3: 2:00pm - 3:00pm"
                                            defaultChecked={act.preferred_slots.includes("Slot 3")}
                                            {...register("actPrefSlots", {required: true})}
                                            value="Slot 3"
                                        />
                                    </div>
                                    <div>
                                        <Form.Check
                                            label="Slot 4: 3:00pm - 4:00pm"
                                            defaultChecked={act.preferred_slots.includes("Slot 4")}
                                            {...register("actPrefSlots", {required: true})}
                                            value="Slot 4"
                                        />
                                    </div>
                                    <div>
                                        <Form.Check
                                            label="Slot 5: 4:00pm - 5:00pm"
                                            defaultChecked={act.preferred_slots.includes("Slot 5")}
                                            {...register("actPrefSlots", {required: true})}
                                            value="Slot 5"
                                        />
                                    </div>
                                </div>
                                {errors.actPrefSlots &&
                                    <span className="pf-validation-error">At least one slot is required</span>}
                            </Card.Body>
                        </Card>

                        <Card>
                            <Card.Body>
                                <div className="mb-3">
                                    <strong>Do you have a preferred venue and time you'd like to perform at?</strong>
                                </div>
                                <div className="mb-3">
                                    If you do, please make sure the venue owner signup for Porchfest. We encourage you to reach
                                    out to the venue owner after they sign up and work out a match. <strong>Note: Preferred venue requests are not guaranteed.</strong>
                                </div>
                                <div>
                                    {/* register your input into the hook by invoking the "register" function */}
                                    <Form.Control
                                        type="text"
                                        defaultValue={act.venue_pref}
                                        // placeholder="Optional: Enter your preferred venue address, if you have one"
                                        {...register("actVenuePref", {required: false})}
                                    />
                                    {/*{errors.actVenuePref && <span className="pf-validation-error">This field is required</span>}*/}
                                </div>
                            </Card.Body>
                        </Card>

                        <Card>
                            <Card.Body>
                                <div className="mb-3">
                                    <strong>Are you able to bring a sound system/mixer?</strong>&nbsp;<span className="fs-5 text text-danger">*</span>
                                </div>
                                <div>
                                    <Form.Select
                                        defaultValue={act.sound_system}
                                        {...register("actSoundSystem", {required: true})}>
                                        {ACT_SOUND_SYSTEM.map((ss, index) => (
                                            <option key={index} value={ss.value}>{ss.name}</option>
                                        ))}
                                    </Form.Select>
                                    {errors.actSoundSystem && <span className="pf-validation-error">This field is required</span>}
                                </div>
                            </Card.Body>
                        </Card>

                        <Card>
                            <Card.Body>
                                <div className="mb-3">
                                    <strong>If we need a rain date (June 15), are you available to perform that day?</strong>&nbsp;<span className="fs-5 text text-danger">*</span>
                                </div>
                                <div>
                                    <div>
                                        <Form.Check
                                            type="radio"
                                            label="Available"
                                            defaultChecked={act.rain_date_available}
                                            {...register("actRainDate", {required: true})}
                                            value="true"
                                        />
                                    </div>
                                    <div>
                                        <Form.Check
                                            type="radio"
                                            label="Unavailable"
                                            defaultChecked={!act.rain_date_available}
                                            {...register("actRainDate", {required: true})}
                                            value="false"
                                        />
                                    </div>
                                </div>
                                {errors.actRainDate &&
                                    <span className="pf-validation-error">This field is required</span>}
                            </Card.Body>
                        </Card>

                        <Row className="mb-3">
                            <Col sm={12}>
                                <Button type="submit" disabled={!editsAllowed}>Save</Button>
                                &nbsp;&nbsp;
                                {/*<Link to="/venues">Cancel</Link>*/}
                                <Button type="button" variant="outline-secondary"
                                        onClick={handleCancel}>Cancel</Button>
                            </Col>
                        </Row>
                    </Form>
                </div>
            }
        </>
    )
};

export default ActEdit;
